import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import html2canvas from "html2canvas";
import * as React from "react";
import { useReactToPrint } from "react-to-print";
import { LazyIcon } from "../../components/LazyIcon";
import { useApi } from "../../containers/ApiContainer";
import { useLoadingProgress } from "../../containers/LoadingProgressContainer";
import { useMeasuringToolState } from "../../containers/MeasuringToolContainer";
import { useMultiHangarState } from "../../containers/MultiHangarContainer";
import { useSnackbar } from "../../containers/SnackbarContainer";
import { usePermissions } from "../../hooks/usePermissions";
import { Permission } from "../../utils/permissions";
import { HangarStack } from "../../screens/Hangar";
import { HOLDING_AREA_ID } from "../../screens/MultiHangar/HoldingArea";
import { useLayout } from "../../screens/Ramp/CollapsibleRampActionCard";
import { INFO_AND_CURRENT_AIRCRAFT_WIDTH } from "../../screens/Ramp/RampPresenter";
import { PlacementService } from "../../services/PlacementService";
import { Hangar, Location, Ramp, Stack as StackType } from "../../types";
import { uuidv4 } from "../../utils";
import { copyImageAndUrlToClipboard } from "../../utils/clipboard";
import mixpanel from "../../utils/mixpanel";
import { createShareableScheduleUrl } from "../../utils/urlUtils";
import { ConfirmationDialog } from "../ConfirmationDialog";
import { Loading } from "../Loading";
import { DRAWER_WIDTH } from "../Nav/SideDrawer";
import { SplitButton } from "../SplitButton";
import { FavoritesButton } from "./FavoritesButton";
import { MultiHangarSelect } from "./MultiHangarSelect";
import { ViewSettings } from "./ViewSettings";

type Props = {
  location: Location<Ramp | Hangar>;
  setLocation: (location: Location<Ramp | Hangar>) => void;
  stack: StackType;
  setStack: (stack: StackType) => void;
  setStacks: (stacks: HangarStack[]) => void;
  onClickUndo: () => void;
  renderOnPrint: (idsToPrint?: string[]) => React.ReactNode;
  isScheduledView?: boolean;
  // Props for scheduled view functionality
  scheduledViewProps?: {
    tenantTripMap: Map<string, string>;
    placementService: PlacementService;
    currentTime: Date;
    onScheduledUpdate: () => void;
  };
};

export const LayoutToolbar: React.FC<Props> = ({
  location,
  setLocation,
  stack,
  setStack,
  setStacks,
  onClickUndo,
  renderOnPrint,
  isScheduledView = false,
  scheduledViewProps,
}) => {
  const { hasPermission } = usePermissions();
  const { postgrest } = useApi();
  const [fireFoxError, setShowFireFoxError] = React.useState<boolean>(false);
  const [copyError, setCopyError] = React.useState<boolean>(false);
  const [copyErrorMessage, setCopyErrorMessage] = React.useState<string>(
    "Failed to copy the image to clipboard. The image may be too large or there might be an issue with background images."
  );
  const [confirm, setConfirm] = React.useState<{
    open: boolean;
    text: string;
    okText: string;
    onOk: (ids?: string[], dumpTo?: string) => void;
    renderSecondaryAction?: boolean;
    okButtonColor?:
      | "primary"
      | "secondary"
      | "success"
      | "error"
      | "info"
      | "warning";
  }>({
    open: false,
    text: "",
    okText: "",
    onOk: () => {},
    renderSecondaryAction: false,
  });
  const measuringToolState = useMeasuringToolState();
  const theme = useTheme();
  const [idsToPrint, setIdsToPrint] = React.useState<string[]>([]);
  const { isMultiHangar, hangarIds, hangars, setHangars } =
    useMultiHangarState();
  const { isDrawerOpen } = useLayout();

  const [showMultiHangarPrint, setShowMultiHangarPrint] =
    React.useState<boolean>(false);
  const [showMultiHangarCopy, setShowMultiHangarCopy] =
    React.useState<boolean>(false);
  const snackbar = useSnackbar();
  const { setShow: setShowLoadingProgress } = useLoadingProgress();

  const [print, setPrint] = React.useState<boolean>(false);
  const [copy, setCopy] = React.useState<boolean>(false);
  const [isPreparingCopy, setIsPreparingCopy] = React.useState<boolean>(false);
  const [, setCopySuccess] = React.useState(false);

  const componentRef = React.useRef<HTMLDivElement>(null);
  const copyRef = React.useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    contentRef: componentRef,
  });

  React.useEffect(() => {
    // on mount clear out any existing measuring tool state
    measuringToolState.setActive(false);
  }, []);

  // Determine disabled actions based on permissions
  const disabled = [];
  if (!hasPermission(Permission.USE_FAVORITES)) {
    disabled.push("favorites");
  }
  // View-only mode is automatically enforced in hasPermission for write operations
  if (!hasPermission(Permission.MODIFY_LAYOUT)) {
    disabled.push("favorites");
    disabled.push("clear");
    disabled.push("undo");
  }

  // Handle location update (for tug selection changes)
  const handleLocationUpdate = React.useCallback(async () => {
    try {
      const tableName = location.type === "hangar" ? "hangar" : "ramp";
      const { data, error } = await postgrest
        .from(tableName)
        .select("*")
        .eq("id", location.id)
        .single();

      if (error) throw error;
      if (data) {
        setLocation({ ...location, ...data });
      }
    } catch (error) {
      console.error("Failed to refresh location:", error);
    }
  }, [location, postgrest, setLocation]);

  // Share URL functionality
  const handleCopyUrl = async () => {
    try {
      const shareableUrl = createShareableScheduleUrl(new Date());
      await navigator.clipboard.writeText(shareableUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000); // Reset after 2 seconds
      snackbar.notify({
        text: "URL copied to clipboard",
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to copy URL:", error);
      // Fallback for older browsers
      try {
        const textArea = document.createElement("textarea");
        textArea.value = createShareableScheduleUrl(new Date());
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
        snackbar.notify({
          text: "URL copied to clipboard",
          severity: "success",
        });
      } catch (fallbackError) {
        console.error("Fallback copy also failed:", fallbackError);
        snackbar.notify({
          text: "Failed to copy URL",
          severity: "error",
        });
      }
    }
  };

  // Enhanced helper function to preload both background images and regular IMG elements
  const preloadBackgroundImages = async (
    element: HTMLElement
  ): Promise<void> => {
    try {
      // Find all elements with background images
      const elementsWithBackgrounds = Array.from(
        element.querySelectorAll("*")
      ).filter((el) => {
        const style = window.getComputedStyle(el);
        return style.backgroundImage && style.backgroundImage !== "none";
      });

      // Add the element itself if it has a background
      const style = window.getComputedStyle(element);
      if (style.backgroundImage && style.backgroundImage !== "none") {
        elementsWithBackgrounds.push(element);
      }

      // Find all img elements too
      const imgElements = Array.from(element.querySelectorAll("img"));

      const totalImages = elementsWithBackgrounds.length + imgElements.length;

      if (totalImages === 0) {
        return Promise.resolve();
      }

      // Create promises for each background image
      const backgroundPromises = elementsWithBackgrounds.map((el, index) => {
        return new Promise<void>((resolve) => {
          const style = window.getComputedStyle(el);
          const bgImage = style.backgroundImage;

          // Skip CSS gradients and other non-URL background values
          if (
            !bgImage ||
            bgImage === "none" ||
            bgImage.includes("gradient") ||
            !bgImage.includes("url(")
          ) {
            resolve();
            return;
          }

          // Extract URL from url(...) syntax
          const bgUrl = bgImage.replace(/url\(['"]?(.*?)['"]?\)/g, "$1");

          if (!bgUrl || bgUrl === "none") {
            resolve();
            return;
          }

          // Retry mechanism for failed loads
          const tryLoadImage = (attempt: number) => {
            const img = new Image();
            img.onload = () => {
              resolve();
            };
            img.onerror = () => {
              if (attempt < 3) {
                setTimeout(() => tryLoadImage(attempt + 1), 1000);
              } else {
                console.error(
                  `Background image ${
                    index + 1
                  } failed after 3 attempts: ${bgUrl}`
                );
                resolve(); // Resolve anyway to continue the process
              }
            };

            // Add a cache-busting parameter to force reload
            img.src = bgUrl.includes("?")
              ? `${bgUrl}&_t=${Date.now()}&attempt=${attempt}`
              : `${bgUrl}?_t=${Date.now()}&attempt=${attempt}`;

            // Set a timeout to resolve anyway after 10 seconds per attempt
            setTimeout(() => {
              if (attempt >= 3) {
                resolve();
              }
            }, 10000);
          };

          tryLoadImage(1);
        });
      });

      // Create promises for each img element
      const imgPromises = imgElements.map((img, index) => {
        return new Promise<void>((resolve) => {
          if (img.complete && img.naturalWidth > 0) {
            resolve();
            return;
          }

          const onLoad = () => {
            img.removeEventListener("load", onLoad);
            img.removeEventListener("error", onError);
            resolve();
          };

          const onError = (e: ErrorEvent) => {
            console.error(`IMG element ${index + 1} failed to load:`, e);
            img.removeEventListener("load", onLoad);
            img.removeEventListener("error", onError);
            resolve(); // Resolve anyway to continue
          };

          img.addEventListener("load", onLoad);
          img.addEventListener("error", onError);

          // Set a timeout to resolve anyway after 15 seconds
          setTimeout(() => {
            img.removeEventListener("load", onLoad);
            img.removeEventListener("error", onError);
            resolve();
          }, 15000);
        });
      });

      // Wait for all images to load or timeout
      await Promise.all([...backgroundPromises, ...imgPromises]);
    } catch (error) {
      console.error("Error in preloadBackgroundImages:", error);
      // Don't throw - we want the copy process to continue even if preloading fails
    }
  };

  const infoDrawerWidth = isDrawerOpen ? INFO_AND_CURRENT_AIRCRAFT_WIDTH : 125;
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        pt: 1,
        pl: 1,
        maxWidth: `calc(100vw - ${DRAWER_WIDTH}px - ${infoDrawerWidth}px - 2px - 8px)`,
        zIndex: theme.zIndex.modal - 1,
      }}
    >
      <ViewSettings
        locationType={location.type}
        isScheduledView={isScheduledView}
        location={location}
        onLocationUpdate={handleLocationUpdate}
      />
      <Stack direction="row" alignItems="center" spacing={1}>
        {!isMultiHangar && (
          <Tooltip title="Measuring Tool" placement="left">
            <Button
              disabled={disabled.includes("measuring")}
              color={measuringToolState.active ? "success" : "info"}
              variant="contained"
              onClick={() => {
                measuringToolState.setActive(!measuringToolState.active);
              }}
            >
              <LazyIcon name="StraightenIcon" />
            </Button>
          </Tooltip>
        )}
        {stack.name !== "reference" && !isScheduledView && (
          <FavoritesButton
            disabled={disabled.includes("favorites")}
            location={location}
            setLocation={setLocation}
            stacks={location.stacks.filter(
              (s) =>
                Boolean(s?.name) &&
                ["current", "reference"].indexOf(s.name || "") === -1
            )}
          />
        )}
        <SplitButton
          disabled={disabled.includes("clear")}
          color="info"
          options={[
            // Only show Clear option when not in scheduled view
            ...(isScheduledView
              ? []
              : [
                  {
                    label: "Clear",
                    disabled: false,
                    onClick: () => {
                      setStacks([]);
                      const text = isMultiHangar
                        ? `This will remove all aircraft listed in the selected hangars.`
                        : isScheduledView
                        ? `☢️ NUCLEAR OPTION: This will unassign ALL aircraft from this ${location.type} from the current time through the end of their trips. This cannot be undone easily. Are you sure?`
                        : `This will remove all aircraft listed in this ${location.type}.`;
                      const onOk = isMultiHangar
                        ? (ids?: string[]) => {
                            if (!ids) return;
                            setHangars(
                              hangars.map((h) =>
                                ids.includes(h.id)
                                  ? {
                                      ...h,
                                      stack: {
                                        ...h.stack,
                                        tenants: [],
                                        movableObstacles: [],
                                      },
                                    }
                                  : h
                              )
                            );
                          }
                        : async () => {
                            setStacks([]);

                            if (isScheduledView && scheduledViewProps) {
                              // Nuclear option for scheduled views - clear all trips from current time forward
                              const {
                                tenantTripMap,
                                currentTime,
                                onScheduledUpdate,
                              } = scheduledViewProps;

                              try {
                                for (const tenant of stack.tenants) {
                                  const tripId = tenantTripMap.get(tenant.id);
                                  if (tripId) {
                                    // Use stored procedure to unassign trip from current time forward
                                    const { error } = await postgrest.rpc(
                                      "assign_trip_to_location",
                                      {
                                        p_trip_id: tripId,
                                        p_location_id: null, // Unassigned
                                        p_location_type: null, // Unassigned
                                        p_start_time: currentTime.toISOString(),
                                        p_end_time: null, // Through end of trip
                                      }
                                    );

                                    if (error) {
                                      console.error(
                                        `Failed to unassign trip ${tripId}:`,
                                        error
                                      );
                                    }
                                  }
                                }

                                // Trigger refresh to show cleared placements
                                onScheduledUpdate();
                              } catch (error) {
                                console.error(
                                  "Failed to clear scheduled placements:",
                                  error
                                );
                              }
                            } else {
                              // Original logic for current views
                              setStack({
                                ...stack,
                                tenants: [],
                                movableObstacles: [],
                              });
                            }
                          };
                      setConfirm({
                        open: true,
                        text,
                        okText: isScheduledView
                          ? "Yes, clear all trips (NUCLEAR)"
                          : "Yes, remove all aircraft",
                        onOk,
                        okButtonColor: isScheduledView ? "error" : "success",
                      });
                    },
                  },
                ]),
            {
              label: "Dump",
              disabled: false,
              onClick: () => {
                const text = isMultiHangar
                  ? `This will dump all aircraft from the selected hangars.`
                  : isScheduledView
                  ? `This will remove all aircraft from this ${location.type} for their entire trip duration.`
                  : `This will dump all aircraft to the front.`;
                const okText = isMultiHangar
                  ? "Yes, dump hangars"
                  : isScheduledView
                  ? `Yes, remove from ${location.type}`
                  : "Yes, dump hangar";
                const onOk = isMultiHangar
                  ? (ids?: string[], dumpTo?: string) => {
                      if (!ids) return;
                      setStacks([]);
                      if (dumpTo === "HOLDING_AREA") {
                        const holdingAreaHangar = hangars.find(
                          (h) => h.id === HOLDING_AREA_ID
                        );
                        const tenantsForHoldingArea = hangars
                          .filter((h) => ids.includes(h.id))
                          .map((h) => h.stack.tenants)
                          .flat()
                          .map((t) => ({
                            ...t,
                            position: {
                              ...t.position,
                              stack_id: holdingAreaHangar?.stack.id,
                              x: null,
                              y: null,
                              angle: null,
                            },
                          }));

                        setHangars(
                          hangars
                            .map((h) =>
                              ids.includes(h.id)
                                ? {
                                    ...h,
                                    stack: {
                                      ...h.stack,
                                      tenants: [],
                                    },
                                  }
                                : h
                            )
                            .map((h) =>
                              h.id === HOLDING_AREA_ID
                                ? {
                                    ...h,
                                    stack: {
                                      ...h.stack,
                                      tenants: [
                                        ...h.stack.tenants,
                                        ...tenantsForHoldingArea,
                                      ],
                                    },
                                  }
                                : h
                            )
                        );
                        return;
                      }
                      setHangars(
                        hangars.map((h) =>
                          ids.includes(h.id)
                            ? {
                                ...h,
                                stack: {
                                  ...h.stack,
                                  tenants: h.stack.tenants.map((t) => ({
                                    ...t,
                                    position: {
                                      ...t.position,
                                      x: null,
                                      y: null,
                                      angle: null,
                                    },
                                  })),
                                },
                              }
                            : h
                        )
                      );
                    }
                  : async () => {
                      setStacks([]);

                      if (isScheduledView && scheduledViewProps) {
                        // Simplified logic for scheduled views - just clear positions for current aircraft
                        const {
                          placementService,
                          onScheduledUpdate,
                          tenantTripMap,
                          currentTime,
                        } = scheduledViewProps;

                        setShowLoadingProgress(true);
                        try {
                          // Get all tenants and their trip IDs
                          for (const tenant of stack.tenants) {
                            const tripId = tenantTripMap.get(tenant.id);
                            if (!tripId) {
                              console.warn(
                                `No trip ID found for tenant ${tenant.id}`
                              );
                              continue;
                            }

                            // Determine location type and ID from the current tenant position
                            let locationId: string | null = null;
                            let locationType: "hangar" | "ramp" | null = null;

                            if (location.type === "hangar") {
                              locationId = location.id;
                              locationType = "hangar";
                            } else if (location.type === "ramp") {
                              locationId = location.id;
                              locationType = "ramp";
                            }

                            // Clear the position using moveAircraftAtTime with null coordinates
                            await placementService.moveAircraftAtTime(
                              tripId,
                              locationId,
                              locationType,
                              currentTime,
                              {
                                x: null as any,
                                y: null as any,
                                angle: null as any,
                              }
                            );
                          }

                          // Trigger refresh to show updated placements
                          onScheduledUpdate();
                        } catch (error) {
                          console.error(
                            "Failed to dump scheduled placements:",
                            error
                          );
                        } finally {
                          setShowLoadingProgress(false);
                        }
                      } else {
                        // Original logic for current views
                        setStack({
                          ...stack,
                          tenants: stack.tenants.map((t) => ({
                            ...t,
                            position: {
                              ...t.position,
                              x: null as any,
                              y: null as any,
                              angle: null as any,
                            },
                          })),
                        });
                      }
                    };
                setConfirm({
                  open: true,
                  text,
                  okText,
                  onOk,
                  renderSecondaryAction: true,
                });
              },
            },
            // Hide "All Tenants" option in scheduled views since it doesn't apply to trip management
            ...(isScheduledView
              ? []
              : [
                  {
                    label: "All Tenants",
                    disabled: stack.isReference,
                    onClick: async () => {
                      const text = isMultiHangar
                        ? "This will reset the selected hangars to the all tenant hangar layout."
                        : `This will reset to the all tenant ${location.type} layout.`;
                      const onOk = isMultiHangar
                        ? (ids: string[]) => {
                            setStacks([]);
                            const resetHangarToReference = (
                              h: Hangar
                            ): Hangar => {
                              const referenceStack = h.stacks.find(
                                (s) => s?.name === "reference"
                              );
                              return {
                                ...h,
                                stack: {
                                  ...h.stack,
                                  tenants: [...referenceStack.tenants].map(
                                    (t) => ({
                                      ...t,
                                      position: {
                                        ...t.position,
                                        id: uuidv4(),
                                        stack_id: h.stack.id,
                                        entity_id: t.entity_id,
                                      },
                                    })
                                  ),
                                  movableObstacles: [
                                    ...referenceStack.movableObstacles,
                                  ].map((mo) => ({
                                    ...mo,
                                    position: {
                                      ...mo.position,
                                      id: uuidv4(),
                                      stack_id: h.stack.id,
                                      entity_id: mo.entity_id,
                                    },
                                  })),
                                },
                              };
                            };
                            setHangars(
                              hangars.map((h) =>
                                ids.includes(h.id)
                                  ? resetHangarToReference(h)
                                  : h
                              )
                            );
                          }
                        : () => {
                            setStacks([]);
                            const referenceStack = location.stacks.find(
                              (s) => s?.name === "reference"
                            );
                            setLocation({
                              ...location,
                              stack: {
                                ...location.stack,
                                tenants: [...referenceStack.tenants].map(
                                  (t) => ({
                                    ...t,
                                    position: {
                                      ...t.position,
                                      id: uuidv4(),
                                      stack_id: location.stack.id,
                                      entity_id: t.entity_id,
                                    },
                                  })
                                ),
                                movableObstacles: [
                                  ...referenceStack.movableObstacles,
                                ].map((mo) => ({
                                  ...mo,
                                  position: {
                                    ...mo.position,
                                    id: uuidv4(),
                                    stack_id: location.stack.id,
                                    entity_id: mo.entity_id,
                                  },
                                })),
                              },
                            });
                          };

                      setConfirm({
                        open: true,
                        text,
                        okText: "Yes, reset",
                        onOk,
                        renderSecondaryAction: false,
                      });
                    },
                  },
                ]),
          ]}
          startIcon={<LazyIcon name="CleaningServicesIcon" />}
        />
        <Button
          disabled={disabled.includes("undo")}
          variant="contained"
          color="info"
          startIcon={<LazyIcon name="ReplayIcon" />}
          onClick={onClickUndo}
        >
          Undo
        </Button>
        <SplitButton
          disabled={disabled.includes("print")}
          color="info"
          startIcon={<LazyIcon name="PrintIcon" />}
          options={[
            {
              label: "Print",
              disabled: false,
              onClick: () => {
                // if user is on firefox, tell them they can't do this
                if (navigator.userAgent.toLowerCase().includes("firefox")) {
                  setShowFireFoxError(true);
                  return;
                }
                if (isMultiHangar) {
                  setShowMultiHangarPrint(true);
                  return;
                }
                setPrint(true);
                setTimeout(() => {
                  handlePrint();
                  setPrint(false);
                }, 1000);
                mixpanel.track("Print", {
                  location_name: location.name,
                  location_type: location.type,
                });
              },
            },
            {
              label: "Copy",
              disabled: false,
              onClick: () => {
                if (navigator.userAgent.toLowerCase().includes("firefox")) {
                  setShowFireFoxError(true);
                  return;
                }
                if (isMultiHangar) {
                  setShowMultiHangarCopy(true);
                  return;
                }
                mixpanel.track("Copy", {
                  location_name: location.name,
                  location_type: location.type,
                });

                // Show loading state while preparing
                setIsPreparingCopy(true);
                setCopy(true);

                // Use setTimeout to allow the UI to update with increased timeout for complex layouts
                setTimeout(async () => {
                  try {
                    // First preload all background images to ensure they're loaded
                    if (copyRef.current) {
                      await preloadBackgroundImages(copyRef.current);

                      // Additional wait for complex layouts to finish rendering
                      await new Promise((resolve) => setTimeout(resolve, 1000));
                    }

                    // html2canvas is statically imported at top of file for reliability

                    // Enhanced html2canvas configuration for better handling of background images and canvas content
                    const canvas = await html2canvas(copyRef.current, {
                      useCORS: true,
                      allowTaint: true,
                      backgroundColor: null,
                      logging: true, // Enable logging for debugging
                      imageTimeout: 15000, // Longer timeout for image loading
                      scale: window.devicePixelRatio > 1 ? 1 : 2, // Use higher scale on low-DPI screens
                      onclone: (clonedDoc) => {
                        // Force any background images to load before capture
                        const images = clonedDoc.querySelectorAll("img");

                        // Force images to load
                        images.forEach((img) => {
                          if (!img.complete) {
                            img.src = img.src; // Trigger reload
                          }
                        });

                        // Copy canvas content from original document to cloned document
                        const originalCanvases =
                          copyRef.current.querySelectorAll("canvas");
                        const clonedCanvases =
                          clonedDoc.querySelectorAll("canvas");

                        // Improved canvas copying with better error handling and verification
                        for (
                          let i = 0;
                          i <
                          Math.min(
                            originalCanvases.length,
                            clonedCanvases.length
                          );
                          i++
                        ) {
                          try {
                            const originalCanvas = originalCanvases[i];
                            const clonedCanvas = clonedCanvases[i];

                            if (originalCanvas && clonedCanvas) {
                              // Verify canvas dimensions match
                              if (
                                originalCanvas.width === 0 ||
                                originalCanvas.height === 0
                              ) {
                                continue;
                              }

                              // Set the cloned canvas dimensions to match original
                              clonedCanvas.width = originalCanvas.width;
                              clonedCanvas.height = originalCanvas.height;

                              // Get the context of the cloned canvas
                              const clonedContext =
                                clonedCanvas.getContext("2d");

                              if (!clonedContext) {
                                console.error(
                                  `Could not get 2D context for cloned canvas ${
                                    i + 1
                                  }`
                                );
                                continue;
                              }

                              // Copy all canvas properties
                              clonedContext.imageSmoothingEnabled =
                                originalCanvas.getContext("2d")
                                  ?.imageSmoothingEnabled ?? true;
                              clonedContext.imageSmoothingQuality =
                                originalCanvas.getContext("2d")
                                  ?.imageSmoothingQuality ?? "high";

                              // Draw the original canvas onto the cloned canvas with exact dimensions
                              clonedContext.drawImage(
                                originalCanvas,
                                0,
                                0,
                                originalCanvas.width,
                                originalCanvas.height,
                                0,
                                0,
                                clonedCanvas.width,
                                clonedCanvas.height
                              );
                            }
                          } catch (err) {
                            const error = err as Error;
                            console.error(
                              `Error copying canvas ${i + 1} content:`,
                              error
                            );
                          }
                        }

                        return clonedDoc;
                      },
                    });

                    const hangarURL = window.location.href;

                    // Use the updated copyImageAndUrlToClipboard function with error handling
                    const success = await copyImageAndUrlToClipboard(
                      canvas,
                      hangarURL,
                      location.type
                    );

                    if (success) {
                      snackbar.notify({
                        text: "Copied to clipboard",
                        severity: "success",
                      });
                    } else {
                      const detailedError = `Copy failed for ${location.type} "${location.name}". Canvas size: ${canvas.width}x${canvas.height}. Check browser console for detailed logs.`;
                      console.error(detailedError);
                      setCopyErrorMessage(
                        `Failed to copy ${location.type} to clipboard. The image may be too large for your device's memory or there was an issue with aircraft rendering. Try again or check the console for details.`
                      );
                      setCopyError(true);
                    }
                  } catch (err) {
                    const errorDetails = {
                      message: err.message || "Unknown error",
                      stack: err.stack,
                      locationName: location.name,
                      locationType: location.type,
                      tenantCount: stack?.tenants?.length || 0,
                      timestamp: new Date().toISOString(),
                    };

                    console.error("Error during copy operation:", errorDetails);

                    setCopyErrorMessage(
                      `Error copying ${location.type} "${
                        location.name
                      }" to clipboard: ${err.message || "Unknown error"}. ${
                        stack?.tenants?.length || 0
                      } aircraft were being copied. Check console for detailed error information.`
                    );
                    setCopyError(true);
                  } finally {
                    setCopy(false);
                    setIsPreparingCopy(false);
                  }
                }, 3000); // Increased timeout to allow complex layouts to fully render
              },
            },
            {
              label: "Share",
              disabled: false,
              onClick: () => {
                handleCopyUrl();
                mixpanel.track("Share", {
                  location_name: location.name,
                  location_type: location.type,
                });
              },
            },
          ]}
        />
      </Stack>
      {confirm?.open && !isMultiHangar && (
        <ConfirmationDialog
          title={"Please Confirm"}
          onCancel={() => setConfirm(null)}
          onClose={() => setConfirm(null)}
          {...confirm}
        />
      )}
      {print && (
        <Box
          sx={{
            position: "absolute",
            top: "100vh",
          }}
        >
          <div
            ref={componentRef}
            style={{
              display: "block",
              padding: 15,
              marginLeft: 50,
            }}
          >
            <Stack direction="column" justifyContent="space-between">
              <style type="text/css" media="print">
                {`@page { size: ${true ? "portrait" : "landscape"}; }`}
              </style>
              {renderOnPrint(idsToPrint)}
            </Stack>
          </div>
        </Box>
      )}
      <Dialog open={copy || isPreparingCopy} fullScreen>
        <Loading />
      </Dialog>
      {copy && (
        <Box
          sx={{
            // move this thing to Siberia
            position: "absolute",
            top: "200vh",
            left: "200vw",
          }}
        >
          <div
            ref={copyRef}
            style={{
              position: "absolute",
              bottom: 0,
              right: "100%",
              display: "block",
              padding: 15,
              marginLeft: 50,
            }}
          >
            <Stack direction="column" justifyContent="space-between">
              {renderOnPrint(idsToPrint)}
            </Stack>
          </div>
        </Box>
      )}
      {showMultiHangarPrint && (
        <MultiHangarSelect
          title={"Print Hangars"}
          caption={"Select the hangars you would like to print."}
          okText={"Print"}
          hangars={hangars.filter((h) => hangarIds.indexOf(h.id) > -1)}
          onCancel={() => setShowMultiHangarPrint(false)}
          onOk={(idsToPrint) => {
            setPrint(true);
            setIdsToPrint(idsToPrint);
            setTimeout(() => {
              handlePrint();
              setPrint(false);
              setShowMultiHangarPrint(false);
            }, 1000);
            mixpanel.track("Print", {
              location_name: location.name,
              location_type: location.type,
            });
          }}
        />
      )}
      {showMultiHangarCopy && (
        <MultiHangarSelect
          title={"Copy Hangars"}
          caption={"Select the hangars you would like to copy."}
          okText={"Copy"}
          hangars={hangars.filter((h) => hangarIds.indexOf(h.id) > -1)}
          onCancel={() => setShowMultiHangarCopy(false)}
          onOk={(idsToPrint) => {
            // Show loading state while preparing
            setIsPreparingCopy(true);
            setCopy(true);
            setIdsToPrint(idsToPrint);

            // Increased timeout for multi-hangar layouts which are typically more complex
            setTimeout(async () => {
              try {
                // First preload all background images to ensure they're loaded
                if (copyRef.current) {
                  await preloadBackgroundImages(copyRef.current);

                  // Additional wait for complex multi-hangar layouts to finish rendering
                  await new Promise((resolve) => setTimeout(resolve, 1000));
                }

                // html2canvas is statically imported at top of file for reliability

                // Enhanced html2canvas configuration for better handling of background images and canvas content
                const canvas = await html2canvas(copyRef.current, {
                  useCORS: true,
                  allowTaint: true,
                  backgroundColor: null,
                  logging: true, // Enable logging for debugging
                  imageTimeout: 15000, // Longer timeout for image loading
                  scale: window.devicePixelRatio > 1 ? 1 : 2, // Use higher scale on low-DPI screens
                  onclone: (clonedDoc) => {
                    // Force any background images to load before capture
                    const images = clonedDoc.querySelectorAll("img");

                    // Force images to load
                    images.forEach((img) => {
                      if (!img.complete) {
                        img.src = img.src; // Trigger reload
                      }
                    });

                    // Copy canvas content from original document to cloned document
                    const originalCanvases =
                      copyRef.current.querySelectorAll("canvas");
                    const clonedCanvases = clonedDoc.querySelectorAll("canvas");

                    // Improved canvas copying with better error handling and verification
                    for (
                      let i = 0;
                      i <
                      Math.min(originalCanvases.length, clonedCanvases.length);
                      i++
                    ) {
                      try {
                        const originalCanvas = originalCanvases[i];
                        const clonedCanvas = clonedCanvases[i];

                        if (originalCanvas && clonedCanvas) {
                          // Verify canvas dimensions match
                          if (
                            originalCanvas.width === 0 ||
                            originalCanvas.height === 0
                          ) {
                            continue;
                          }

                          // Set the cloned canvas dimensions to match original
                          clonedCanvas.width = originalCanvas.width;
                          clonedCanvas.height = originalCanvas.height;

                          // Get the context of the cloned canvas
                          const clonedContext = clonedCanvas.getContext("2d");

                          if (!clonedContext) {
                            console.error(
                              `Could not get 2D context for multi-hangar cloned canvas ${
                                i + 1
                              }`
                            );
                            continue;
                          }

                          // Copy all canvas properties
                          clonedContext.imageSmoothingEnabled =
                            originalCanvas.getContext("2d")
                              ?.imageSmoothingEnabled ?? true;
                          clonedContext.imageSmoothingQuality =
                            originalCanvas.getContext("2d")
                              ?.imageSmoothingQuality ?? "high";

                          // Draw the original canvas onto the cloned canvas with exact dimensions
                          clonedContext.drawImage(
                            originalCanvas,
                            0,
                            0,
                            originalCanvas.width,
                            originalCanvas.height,
                            0,
                            0,
                            clonedCanvas.width,
                            clonedCanvas.height
                          );
                        }
                      } catch (err) {
                        console.error(
                          `Error copying multi-hangar canvas ${i + 1} content:`,
                          err
                        );
                      }
                    }

                    return clonedDoc;
                  },
                });

                const hangarURL = window.location.href;

                // Use the updated copyImageAndUrlToClipboard function with error handling
                const success = await copyImageAndUrlToClipboard(
                  canvas,
                  hangarURL,
                  location.type
                );

                if (success) {
                  snackbar.notify({
                    text: "Copied to clipboard",
                    severity: "success",
                  });
                } else {
                  const detailedError = `Multi-hangar copy failed. Canvas size: ${canvas.width}x${canvas.height}. Selected hangars: ${idsToPrint.length}. Check browser console for detailed logs.`;
                  console.error(detailedError);
                  setCopyErrorMessage(
                    `Failed to copy multi-hangar layout to clipboard. The image may be too large for your device's memory or there was an issue with aircraft rendering. Selected ${idsToPrint.length} hangars. Try selecting fewer hangars or check the console for details.`
                  );
                  setCopyError(true);
                }
              } catch (err) {
                const errorDetails = {
                  message: err.message || "Unknown error",
                  stack: err.stack,
                  locationName: location.name,
                  locationType: location.type,
                  selectedHangars: idsToPrint?.length || 0,
                  hangarIds: idsToPrint,
                  timestamp: new Date().toISOString(),
                };

                console.error(
                  "Error during multi-hangar copy operation:",
                  errorDetails
                );

                setCopyErrorMessage(
                  `Error copying multi-hangar layout to clipboard: ${
                    err.message || "Unknown error"
                  }. Selected ${
                    idsToPrint?.length || 0
                  } hangars. Check console for detailed error information.`
                );
                setCopyError(true);
              } finally {
                setCopy(false);
                setIsPreparingCopy(false);
                setShowMultiHangarCopy(false);
              }
            }, 3000); // Increased timeout for complex multi-hangar layouts

            mixpanel.track("Copy", {
              location_name: location.name,
              location_type: location.type,
            });
          }}
        />
      )}
      {confirm?.open && isMultiHangar && (
        <MultiHangarSelect
          title={"Please Confirm"}
          caption={confirm.text}
          okText={confirm.okText}
          hangars={hangars.filter((h) => hangarIds.indexOf(h.id) > -1)}
          onCancel={() => setConfirm(null)}
          renderSecondaryAction={confirm.renderSecondaryAction}
          onOk={(idsToPrint, dumpTo) => {
            confirm.onOk(idsToPrint, dumpTo);
            setConfirm(null);
          }}
        />
      )}
      {
        <Dialog open={fireFoxError} onClose={() => setShowFireFoxError(false)}>
          <DialogTitle>Feature Not Supported</DialogTitle>
          <DialogContent>
            <Typography>
              This feature is not supported on Firefox. If you wish to use this
              feature, please use a different browser.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowFireFoxError(false)}>OK</Button>
          </DialogActions>
        </Dialog>
      }
      {
        <Dialog open={copyError} onClose={() => setCopyError(false)}>
          <DialogTitle>Copy Failed</DialogTitle>
          <DialogContent>
            <Typography>{copyErrorMessage}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCopyError(false)}>OK</Button>
          </DialogActions>
        </Dialog>
      }
    </Stack>
  );
};
